import { optionActions } from 'core/Option';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
    ButtonFooter,
    Checkbox,
    ExpandButton,
    FormGroup,
    Input,
    Modal,
    Table,
    TagList
} from 'mol-ui';
import {
    Container,
    InputContainer,
    TableContainer
} from './styled';
import React, { useEffect } from 'react';
import {
    parseBool,
    toggleElementArray,
    validationSchema
} from 'shared/utils';
import {
    useDispatch,
    useErrors,
    useObjectState,
    useSelector
} from 'hooks';

const DEFAULT_WORKPLACE = {
    active: false,
    email: '',
    emails: '',
    name: ''
};

const WorkplaceListing = () => {

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { clearError, clearErrors, errors, setErrors } = useErrors();
    const { dangerColor, successColor } = useTheme();
    const [selectedWorkplace, setSelectedWorkplace, setValue] = useObjectState(null);
    const emailsArray = selectedWorkplace?.emails ? selectedWorkplace?.emails.split(',') : [];
    const addEmailsDisabled = !selectedWorkplace?.email || emailsArray.includes(selectedWorkplace?.email);

    const {
        workplaces,
        workplacesError,
        workplacesLoading,
        workplacesStatus
    } = useSelector('option');

    useEffect(() => {
        setSelectedWorkplace(null);
    }, [workplaces, setSelectedWorkplace]);

    useEffect(() => {
        dispatch(optionActions.getWorkplaces());
    }, []);

    useEffect(() => {
        if (!!selectedWorkplace) return;
        dispatch(optionActions.setOptionWorkplacesError({}));
        clearErrors();
    }, [selectedWorkplace]);
    
    const actionController = [{
        icon: 'edit',
        onClick: setSelectedWorkplace,
        title: t('Editar')
    }];

    const handleAddEmails = () => {
        if (addEmailsDisabled) return;
        handleChange('emails', selectedWorkplace?.email, { clearEmail: true });
    };

    const handleChange = (id, value, options) => {
        clearError(id);
        if (!!workplacesError[id]) {
            const newErrors = { ...workplacesError };
            delete newErrors[id];
            dispatch(optionActions.setOptionWorkplacesError(newErrors));
        }
        if (id === 'emails') {
            if (value === '') return;
            if (options?.clearEmail) setValue('email', '');
            return setValue(id, toggleElementArray(emailsArray, value).join(','));
        }
        setValue(id, value);
    };

    const onSubmit = () => {
        if (!validate()) return;
        const data = {
            ...selectedWorkplace,
            active: selectedWorkplace?.active ? 1 : 0
        };
        dispatch(optionActions.upsertWorkplace(data));
    };

    const validate = () => {
        const { errors, isValid } = validationSchema({
            name: [!!selectedWorkplace?.name, t('Requerido')]
        });
        setErrors(errors);
        return isValid;
    };

    const HEADERS = [{
        flex: 2,
        key: 'name',
        label: t('Lugar de trabajo')
    }, {
        accessor: active => parseBool(active),
        format: ({ active }) => ({
            center: true,
            color: active ? successColor : dangerColor
        }),
        key: 'active',
        label: t('Activo')
    }, {
        flex: 3,
        key: 'emails',
        label: t('Emails')
    }];

    return (
        <Container>
            <Modal active={ !!selectedWorkplace }>
                <FormGroup label={ t('Nombre') } width="300px">
                    <Input
                        error={ errors?.name ?? workplacesError?.name }
                        value={ selectedWorkplace?.name }
                        width="100%"
                        onChange={ value => handleChange('name', value) }
                    />
                </FormGroup>
                <FormGroup label={ t('Emails') } margin="0px 0px 20px 0px" width="300px">
                    <InputContainer>
                        <Input
                            margin="0px 5px 0px 0px"
                            value={ selectedWorkplace?.email }
                            width="255px"
                            onChange={ value => handleChange('email', value) }
                            onEnterPress={ handleAddEmails }
                        />
                        <ExpandButton
                            circle
                            disabled={ addEmailsDisabled }
                            icon="add"
                            size={ 40 }
                            onClick={ handleAddEmails }
                        />
                    </InputContainer>
                    <TagList
                        direction="column"
                        items={ emailsArray.map(email => ({ label: email })) }
                        onRemove={ ({ label }) => handleChange('emails', label) }
                    />
                    <Checkbox
                        checked={ !!selectedWorkplace?.active }
                        label={ t('Activo') }
                        margin="10px 40px 10px 0px"
                        onClick={ checked => handleChange('active', checked) }
                    />
                </FormGroup>
                <ButtonFooter>
                    <ExpandButton
                        danger
                        caption={ t('Cancelar') }
                        disabled={ workplacesStatus === 'loading' }
                        size={ 80 }
                        onClick={ () => setSelectedWorkplace(null) }
                    />
                    <ExpandButton
                        caption={ selectedWorkplace?.id ? t('Editar') : t('Crear') }
                        loading={ workplacesStatus === 'loading' }
                        size={ 80 }
                        onClick={ onSubmit }
                    />
                </ButtonFooter>
            </Modal>
            <TableContainer>
                <Table
                    config={ HEADERS }
                    data={ workplaces }
                    loading={ workplacesLoading }
                    rowActions={ actionController }
                />
            </TableContainer>
            <ButtonFooter>
                <ExpandButton
                    caption={ t('Nuevo lugar de trabajo') }
                    icon="add"
                    size={ 100 }
                    width={ 200 }
                    onClick={ () => setSelectedWorkplace(DEFAULT_WORKPLACE) }
                />
            </ButtonFooter>
        </Container>
    );
};

export default WorkplaceListing;
